import type {
  BasketItem,
  Category,
  Product as CoreProduct,
  Product,
  ShopUser,
  Variant,
  WishlistItem,
  Price,
} from '@scayle/storefront-nuxt'
import type { AgsBasketItem } from '#layers/base/types/basket'

export type AuthTrackingEvent =
  | 'login'
  | 'logout'
  | 'sign_up'
  | 'forgot_password'
  | 'reset_password'
  | 'guest_login'

export const ECOMMERCE_TRACKING_EVENTS = [
  'view_item',
  'view_item_list',
  'view_promotion',
  'add_to_wishlist',
  'remove_from_wishlist',
  'add_to_cart',
  'remove_from_cart',
  'select_item',
  'select_promotion',
  'view_cart',
  'begin_checkout',
  'back_in_stock',
] as const
export type EcommerceTrackingEvent = (typeof ECOMMERCE_TRACKING_EVENTS)[number]

export type BasicTrackingEvent =
  | 'shop_init'
  | 'shop_change'
  | 'customer_data'
  | 'content_view'

export type CheckoutTrackingEvent =
  | 'checkout_reached'
  | 'begin_checkout'
  | 'finish_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'

export type CheckoutType = 'guest' | 'existing_user' | 'new_registration'

export type CheckoutNativeEcommerceItem = {
  item_id: string
  item_product_reference_key: string
  item_name: string
  price: number
  item_brand: string
  item_brand_id: string
  item_category: string
  item_category_id: string
  item_variant: string
  item_variant_reference_key: string
  coupon: string
  sale: boolean
  index: number
  quantity: string
  total_quantity: string
}

export type CheckoutNativeCompleteCheckoutEvent = {
  correlation_id?: string
  event: 'complete_checkout'
  action: string
  label?: string
}

export type CheckoutPaymentType =
  | 'standard_invoice'
  | 'paypal_instant'
  | 'adyen_creditcard'
  | 'adyen_ideal'
  | 'standard_cheque'

export type CheckoutNativeFeatureEvent = {
  action: string
  correlation_id: string
  event: 'feature'
  name: string
  label: string
  shipping_type?: CheckoutNativeShippingTierLabel
}

// Note: the feature event uses these homeDelivery and collectionPoint
// in the label field if the action is click.
// If the action is submit, it uses carrier and collectionPoint in the shipping_type field.
export type CheckoutNativeShippingTierLabel =
  | 'homeDelivery'
  | 'carrier'
  | 'collectionPoint'

export type CheckoutShippingTier = 'home_delivery' | 'packstation'

export type AdditionalTrackingEvent = 'view_cart' | 'view_wishlist' | 'search'

export type CustomTrackingEvent = 'catalogue_order'

export type NavigationTrackingEvent = 'navigation_click'

export type BlogTrackingEvent =
  | 'blog_tag_click'
  | 'blog_article_click'
  | 'blog_themenwelt_click'

type PurchaseTrackingEvent = 'purchase'

export type TrackingEvent =
  | EcommerceTrackingEvent
  | BasicTrackingEvent
  | AdditionalTrackingEvent
  | CheckoutTrackingEvent
  | PurchaseTrackingEvent
  | CustomTrackingEvent
  | NavigationTrackingEvent
  | BlogTrackingEvent
  | 'login'
  | 'logout'
  | 'sign_up'
  | 'newsletter_submit'
  | 'virtual_page_view'
  | 'youtube'
  | `odoscope/error/${string}`
  | `odoscope/timeout/${string}`
  | `odoscope/empty/${string}`
  | `odoscope/control/${string}`
  | `odoscope/odoscope/${string}`
  | 'experiment_triggered'
  | 'search_suggestions_click'
  | 'start_search'
  | 'no_search_suggestion'
  | 'view_search_result'
  | 'goldner_club_submit'
  | 'choose_one_size_dialog_seen'
  | 'choose_one_size_dialog_kept_all_items'
  | 'choose_one_size_dialog_reduced_items'
  | 'choose_one_size_dialog_dismissed'
  | 'choose_one_size_dialog_not_shown'

export type PageType =
  | 'browse'
  | 'navigation'
  | 'discovery'
  | 'category_overview'
  | 'adp'
  | 'wishlist'
  | 'brand'
  | 'brand_overview'
  | 'story'
  | 'story_overview'
  | 'outfit'
  | 'outfit_overview'
  | 'help'
  | 'offCanvas'
  | 'other'
  | 'home'
  | 'category'
  | 'detail'
  | 'search'
  | 'basket'
  | 'checkout'
  | 'confirmation'
  | 'static'
  | 'account'
  | string

export interface TrackingCategory {
  name: string
  id: string
}

export interface ListItem {
  name: string
  id?: string
  index?: number | undefined
  source?: 'plp'
}

export interface ProductListData {
  product: CoreProduct
  category?: TrackingCategory | Category
  list?: ListItem
  quantity?: number
}

export interface ProductViewData extends ProductListData {
  destination: string
  destinationUrl: string
  source: string
  position?: number
}

export interface MultipleActionData {
  currencyCode?: string
  items: ProductListData[]
}

export interface ProductActionData {
  product: CoreProduct & { index?: number }
  variant?: Variant
  category?: TrackingCategory
  quantity?: number
  list?: ListItem
  currencyCode: string
  index?: number
}

export interface ProductInfo {
  item_id: string
  item_name: string
  price: number
  sale_discount: number
  campaign_discount: number
  original_price: number
  item_brand: string
  item_brand_id: string
}

export interface AdditionalInfo {
  item_category?: string
  item_category_id?: string
  item_variant?: string
  quantity?: string | undefined
  item_list_name?: string | null
  item_list_id?: string | null
  index?: number | null
}

export interface ViewInfo extends AdditionalInfo {
  source: string
  destination: string
  destination_url: string
  sold_out?: boolean
  bi_price: string
}

export interface ShopData {
  shop_id: string | number
  shop_gender: string
  locale: string
  shop_currency: string
}

export interface FilterData {
  action: string
  label: string
  sortType?: string
}

export interface BasketData {
  items: BasketItem[]
  total_campaign_reduction_with_tax: number
  total_sale_reduction_with_tax: number
  total_with_tax: number
  total_without_tax: number
}
export interface WishlistData {
  items: WishlistItem[]
  total_campaign_reduction_with_tax: number
  total_sale_reduction_with_tax: number
  total_with_tax: number
  total_without_tax: number
}

export interface PromotionItem {
  item_id: string
  item_name: string
  promotion_id: string
  promotion_name: string
  creative_name: string
  creative_slot: string
  location_id: string
  index: number
}

export interface PromotionData {
  items: PromotionItem[]
}

export interface ShopInitData extends ShopData {
  landing_page: string
  shop_version: string
  parameter: string
  referrer: string
  deeplink: string
}

export interface PageViewData {
  content_name: string
  page_type: PageType
  title: string
  page_type_id: string
  click_origin?: string
}

export interface SearchData {
  search_term: string
  search_term_completed: string
  search_action: string
  search_destination: string
}

export interface CustomerData {
  user_id: number
  login_status: 'logged_in' | 'logged_out'
  customer_type: 'guest' | 'new' | 'existing'
  user_type: 'new' | 'returning' | 'registered'
  eh: string
  emailHash: string
}

export interface CustomerInfo {
  method?: 'none' | 'standard_email'
  user?: CustomerData
  newsletter_signup?: boolean
  newsletter_email?: string
  goldner_club_email?: string
}

export interface CustomerLogoutData {
  customer_id?: number
  eh: string
}

export interface DatalayerData {
  login_status?: 'logged_in' | 'logged_out'
  session_id?: string
  page_category?:
    | 'home'
    | 'category_1'
    | 'category_2'
    | 'category_3'
    | 'cart'
    | 'checkout'
    | 'orderConfirmation'
    | 'product'
    | 'searchResult'
    | 'ags'
    | 'support'
    | 'customerService'
    | 'blog'
    | 'other'
  page_title?: string
  page_path?: string
  pageReferrer?: string
  userType?: 'new' | 'returning' | 'registered'
  userId?: string
  customerType?: 'new' | 'existing'
  trafficType?: 'internal' | 'external'
  site?: string
  businessUnit?: string
}

export type CustomData = object

export interface EcommerceTrackingAttributesData {
  brand?: string
  category?: string
  currency?: string
  isSale?: boolean
  name?: string
  newCustomer?: boolean
  price?: number
  sku?: string
  stock?: number
  quantity?: number
}

export interface EcommerceTrackingItemData {
  currency?: string
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_id?: string
  item_name?: string
  item_variant?: string
  price?: number
  quantity?: number
}

export interface EcommerceTrackingPayload {
  attributes?: EcommerceTrackingAttributesData
  ecommerce: {
    items: EcommerceTrackingItemData[]
  }
}

export interface YouTubeTrackingPayload {
  id: string
  title: string
  status: string
  creative_slot?: string
  creative_name?: string
}

export interface BlogTagTrackingPayload {
  blog_tag: string
  blog_section: string
}

export interface BlogArticleTrackingPayload {
  article_title: string
  teaser_type: string
  blog_section: string
}

export interface BlogThemenweltTrackingPayload {
  blog_article_category: string
  blog_section: string
}

export type TrackingPayload =
  | ProductActionData
  | MultipleActionData
  | ProductViewData
  | ProductListData
  | ShopData
  | ShopInitData
  | PageViewData
  | CustomerData
  | CustomerInfo
  | CustomerLogoutData
  | SearchData
  | FilterData
  | BasketData
  | WishlistData
  | PromotionData
  | DatalayerData
  | CustomData
  | YouTubeTrackingPayload

export interface AdditionalItem extends AdditionalInfo {
  item_id: string
  item_name: string
  price: number
  sale_discount: number
  campaign_discount: number
  original_price: number
  item_brand: string
  item_brand_id: string
  quantity: string
}

export type BasketItemWithCoupon = AgsBasketItem & {
  promotionCode?: string
}

export type TrackBasketParams = {
  basketItems: AgsBasketItem[]
}

export type TrackAddToBasketParams = {
  product: Product
  variant: Variant
  quantity?: number
  coupon?: string
  type:
    | 'pdp'
    | 'direct_order'
    | 'advantage_page'
    | 'increase_item_quantity'
    | 'recommendation_in_cart'
    | 'wishlist'
    | 'outfitpage'
}

export type TrackRemoveFromBasketParams = {
  product: Product
  variant: Variant
  quantity: number
  coupon?: string
  type:
    | 'decrease_cart'
    | 'delete_cart'
    | 'decrease_checkout'
    | 'delete_checkout'
}

export type TrackCustomerDataParams = {
  user?: ShopUser
  customerType: 'new' | 'guest' | 'existing'
  isLoggedIn: boolean
  biVp?: boolean
  biSc?: number
}

// Products that render in a list and as such have an index
export type ListedProduct = Product & { index: number }

export type ListItemType =
  | 'search_results'
  | 'category_page'
  | 'lastseen'
  | 'similar_products'
  | 'top_seller'
  | 'outfit'
  | 'inspiration'
  | 'shop_the_look'
  | 'cart_layer'
  | 'new_products'
  | 'also_purchased'

export type TrackViewItemListEventParams = {
  type: ListItemType
  name: string
  items: ListedProduct[]
  filters: string
  filterOptions: string
  sorting: string
  totalItemQuantity: number
  totalPages: number
  viewItemQuantity: number
  currentPage: number
}

export type TrackSelectItemEventParams = {
  product: Product
  index: number
  listName: string
  listType: string
}

export type TrackViewItemParams = {
  product: Product
  variant?: Variant
  listName?: string
  index?: number
}

export type SearchAction =
  | 'search_hotkey'
  | 'show_all_results'
  | 'search_icon'
  | 'category_click'
  | 'product_click'

export type SearchType = 'category' | 'product' | 'search'

export type TrackSearchEventParams = {
  searchTerm: string
  originalSearchTerm: string
  originalSearchTermLength: number
  searchAction: SearchAction
  searchType: SearchType
  searchSuggestedCategories: string[]
  searchSuggestedProducts: string[]
  numSearchSuggestions: number
  searchDestination: string
}

export type TrackNoSearchSuggestionEventParams = {
  originalSearchTerm: string
}

export type TrackViewSearchResultEventParams = {
  searchTerm: string
  numSearchResults: number
  ecommerceItems: any[]
}

export type TrackSearchSuggestionEventParams = {
  searchTerm: string
  suggestion: string
  position: number
  section: string
}

export type TrackContentViewEventParams = {
  contentName: string
  title: string
  pageType: PageType
  pageTypeId: string
  clickOrigin: string
}

export type TrackAddToWishListParams = {
  product: Product
  variant?: Variant
  index?: number
  listName?: string
  listType?: ListItemType
  type: 'pdp' | 'item_list' | 'cart' | 'advantage_page'
}

export type TrackRemoveFromWishListParams = {
  product: Product
  variant?: Variant
}

export type TrackWishListParams = {
  wishlistItems: WishlistItem[]
}

export type OrderItemVariant = {
  id: number
}
export type OrderItemProduct = {
  name: string
  variant: OrderItemVariant
  price: Price
}
